import { useMutation } from '@apollo/client';
import { Editor } from '@tiptap/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLatest } from 'react-use';

import { cacheUtils } from '@eluve/apollo-client';
import { AmendmentHeader, useTypingThrottle } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { EluveEditor } from '@eluve/eluve-editor';
import { useAmendments } from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { TextFieldBlock } from '@eluve/llm-outputs';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { useAnalytics } from '@eluve/posthog-react';
import { areStringsEqualWithoutSpecialCharacters } from '@eluve/utils';

import { useUserName } from './useUserName';

const updateChartAmendmentMutation = graphql(`
  mutation updateChartAmendmentMutation(
    $tenantId: uuid!
    $chartAmendmentId: uuid!
    $value: String!
  ) {
    updateChartAmendmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $chartAmendmentId }
      _set: { value: $value }
    ) {
      __typename
      id
      value
    }
  }
`);

const deleteChartAmendmentMutation = graphql(`
  mutation deleteChartAmendmentMutation(
    $tenantId: uuid!
    $chartAmendmentId: uuid!
  ) {
    deleteChartAmendmentsByPk(id: $chartAmendmentId, tenantId: $tenantId) {
      __typename
      id
    }
  }
`);

export const Amendment: React.FC<{
  appointmentId: string;
  chartAmendmentId: string;
  block: TextFieldBlock;
}> = ({ appointmentId, chartAmendmentId, block }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { label, text } = block;
  const analytics = useAnalytics('amendments');

  const { amendments } = useAmendments(appointmentId);
  const chartAmendments = amendments
    .map((amendment) => amendment.chart_amendments)
    .flat();
  const chartAmendment = chartAmendments.find(
    (amendment) => amendment.id === chartAmendmentId,
  );
  const amendment = amendments.find(
    (amendment) => amendment.id === chartAmendment?.amendmentId,
  );

  const [updateChartAmendment] = useMutation(updateChartAmendmentMutation, {
    onError: () => toast.error(t('smartBlocks.amendment.updateFailed')),
    optimisticResponse: (data) => ({
      updateChartAmendmentsByPk: {
        __typename: 'ChartAmendments' as const,
        id: chartAmendmentId,
        value: data.value,
      },
    }),
  });

  const [deleteChartAmendment] = useMutation(deleteChartAmendmentMutation, {
    onError: () => toast.error(t('smartBlocks.amendment.deleteFailed')),
    optimisticResponse: () => ({
      deleteChartAmendmentsByPk: {
        __typename: 'ChartAmendments' as const,
        id: chartAmendmentId,
      },
    }),
    update: () => {
      cacheUtils.evict({
        key: { id: chartAmendmentId },
        typeName: 'ChartAmendments',
      });
    },
  });
  const isReadonly = Boolean(amendment?.submittedAt);

  const date = amendment?.submittedAt ?? chartAmendment?.createdAt ?? '';

  const [forceUpdate, setForceUpdate] = useState(0);
  const amendmentValue = useRef<string | null>(null);

  useEffect(() => {
    amendmentValue.current = chartAmendment?.value ?? '';
  }, [chartAmendment]);

  const getDisplayValue = useCallback(() => {
    return amendmentValue.current ?? chartAmendment?.value ?? '';
  }, [chartAmendment]);

  const updateChartAmendmentValue = useRef(async (notes: string) => {
    if (
      chartAmendment &&
      !areStringsEqualWithoutSpecialCharacters(notes, chartAmendment.value)
    ) {
      await updateChartAmendment({
        variables: {
          tenantId: chartAmendment.tenantId,
          value: notes,
          chartAmendmentId: chartAmendment.id,
        },
      });
    }
  });

  const latestValue = useLatest(chartAmendment?.value);

  const updateChartAmendmentValueThrottle = useTypingThrottle(
    updateChartAmendmentValue.current,
  );

  const onUpdate = useMemo(() => {
    return async (editor: Editor | undefined) => {
      if (latestValue.current === getTiptapMarkdownContentFromEditor(editor)) {
        return;
      }
      if (editor) {
        const content = getTiptapMarkdownContentFromEditor(editor);
        amendmentValue.current = content;
        updateChartAmendmentValueThrottle(content);
      }
    };
  }, [latestValue, updateChartAmendmentValueThrottle]);

  const onDuplicate = async () => {
    if (chartAmendment && text) {
      amendmentValue.current = text;
      try {
        await updateChartAmendment({
          variables: {
            tenantId: chartAmendment.tenantId,
            value: text,
            chartAmendmentId: chartAmendment.id,
          },
        });
      } catch {
        toast.error(t('smartBlocks.amendment.duplicateFailed'));
        amendmentValue.current = chartAmendment.value ?? '';
      } finally {
        setForceUpdate((prev) => prev + 1);
      }
    }
  };
  const onDelete = async () => {
    analytics.capture('user_discarded_section_amendment', {
      chartSectionAmendmentDiscarded: label,
      amendmentId: amendment!.id,
      appointmentId,
      tenantId: chartAmendment!.tenantId,
      userId: amendment!.userId,
    });

    await deleteChartAmendment({
      variables: {
        chartAmendmentId,
        tenantId: chartAmendment!.tenantId,
      },
    });
  };
  const userName = useUserName(amendment?.userId ?? '');

  if (!chartAmendment || !amendment) {
    return null;
  }

  return (
    <div className="w-full border-t">
      <div className="px-6 pt-6">
        <AmendmentHeader
          date={date}
          isReadonly={isReadonly}
          userName={userName}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          duplicateSectionLabel={label}
        />
      </div>
      <EluveEditor
        key={`editor-${forceUpdate}-${amendment?.submittedAt}`}
        content={getDisplayValue()}
        disabled={isReadonly}
        onUpdate={onUpdate}
      />
    </div>
  );
};
